import { useEffect, useState } from 'react';

/**
 * Erkennt ein neues Frontend-Deployment: pollt `/version.json` und vergleicht die
 * dort hinterlegte `buildId` mit der in dieses Bundle einkompilierten `__BUILD_ID__`.
 * Weicht sie ab, läuft eine neuere Version auf dem Server → der Nutzer muss neu laden.
 *
 * Robust gegen DEV/offline: fehlt version.json oder schlägt der Fetch fehl, passiert
 * nichts (kein Banner). Sobald ein Update erkannt wurde, stoppt das Polling.
 */
const POLL_MS = 60_000;

export function useUpdateAvailable(pollMs: number = POLL_MS): boolean {
  const [available, setAvailable] = useState(false);

  useEffect(() => {
    if (available) return; // einmal erkannt → nicht weiter pollen
    let active = true;

    const check = async () => {
      try {
        const res = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
        if (!res.ok) return;
        const data = (await res.json()) as { buildId?: string };
        if (active && data.buildId && data.buildId !== __BUILD_ID__) {
          setAvailable(true);
        }
      } catch {
        /* offline / version.json fehlt (DEV) → ignorieren */
      }
    };

    const id = window.setInterval(check, pollMs);
    const onFocus = () => { void check(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    void check(); // sofortige Erstprüfung

    return () => {
      active = false;
      window.clearInterval(id);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [available, pollMs]);

  return available;
}
